import {
  PD_SAMPLE_FILE_NAME,
  PD_SAMPLE_ROWS,
  PD_SAMPLE_SHEET
} from '@service-library/modules/wms/clusters/warehouse-setup-tat/constants/pendataanSample.constants'
import {
  labelProposal,
  normName,
  PD_FROM_MASTER,
  PD_STD,
  PD_UNIT_STATUS,
  type PdCategory,
  pdCategoryOf
} from '../handlers/pendataanRules'
import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { materialId, purchaseUnitOf, smallPerBigOf, warehouseRmId } from './wms-master.seed'

/**
 * Warehouse Setup C15 v7.50 (TAT-01…04, laporan 30). Pendataan Awal = impor Excel → kemasan → stiker → opname pertama;
 * tidak ada lagi stok awal tanpa label (`SAW-`), temuan "di luar rak", atau pembekuan gudang.
 *
 * Keadaan demo (jam demo 18 Agu 2026 15:13):
 * - Sesi PDW-260818-001 BERJALAN dari berkas contoh `saldo-material.xlsx` (235 material bersaldo, 4 baris saldo 0
 *   dilewati). ETK-BOLD12 dan ETK-XTRA20 (yang ada di master) sudah Disetujui (isi 33.000 pcs per Box) dan stikernya
 *   tercetak (20 + 6 kemasan); material lain masih "Belum ada di master".
 * - Opname pertama sudah dimulai: R01 & R03 untuk Karno, R02 untuk Tono. R02 sudah dikirim (6 kemasan ETK-XTRA20 di dua
 *   posisi, dua posisi kosong); Karno baru men-scan 8 kemasan ETK-BOLD12 di R01.
 * - Pindah Material: tiga unit berlabel di rak Tembakau R01 melanggar aturan kategori ("Belum Patuh"); FOIL-GLD70 sudah
 *   masuk tugas PDH-260817-001 untuk Karno (CTP-BOLD, TT-GLDH, FOIL-GLD70 — material tanpa stok lain di seed).
 *
 * Kolom tambahan (ERD bagian 19): `initial_capture_session.{import_meta, opname_started_*, closed_*, approved_*,
 * return_note, returned_*, result}`; `initial_capture_area.{operator_id, submitted_at}`; tabel `initial_capture_scan`;
 * `rm_opening_stock` sebagai baris pemetaan; `rm_unit.{opening_line_id, capture_session_id}` + status Pendataan Awal.
 */

const utc = (at: string) => new Date(`${at.replace(' ', 'T')}:00+07:00`).toISOString()
const audit = (userId: string | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})
const pad = (value: number, width: number) => String(value).padStart(width, '0')

/* ------------------------------------------------------------ alasan temuan RM (`mt_reject_type` kategori RM_FINDING) */

/** Alasan Not Good opname (prototype master alasan kategori `temuan`). */
export const RM_FINDING_REASONS = [
  ['LEMBAB', 'Lembab / Berjamur'],
  ['SOBEK', 'Kemasan Sobek'],
  ['KOTOR', 'Terkontaminasi / Kotor'],
  ['SUSUT', 'Susut Mutu']
] as const
export const RM_FINDING_CATEGORY = 'RM_FINDING'
export const rmFindingReasonId = (code: string) =>
  seedId(SEED_GROUP.rejectType, 200 + RM_FINDING_REASONS.findIndex(([c]) => c === code))

/* ------------------------------------------------------------ demo Pendataan Awal */

export const PD_DEMO_SESSION_NO = 'PDW-260818-001'
export const pdDemoSessionId = seedId(SEED_GROUP.initialCaptureSession, 1)
/** Material contoh yang sudah ada di master: kemasan Box isi 33.000 pcs (standar Master Material). */
const DEMO_APPROVED: Record<string, { qty: number; printedAt: string }> = {
  'ETK-BOLD12': { qty: 33000, printedAt: '2026-08-18 09:20' },
  'ETK-XTRA20': { qty: 33000, printedAt: '2026-08-18 09:25' }
}
/** Hasil scan contoh: [kode material, urutan kemasan (1-based), posisi, operator, waktu]. */
const DEMO_SCANS: [string, number, string, 'karno' | 'tono', string][] = [
  ['ETK-XTRA20', 1, 'R02-B01-L01-P01', 'tono', '2026-08-18 10:32'],
  ['ETK-XTRA20', 2, 'R02-B01-L01-P01', 'tono', '2026-08-18 10:33'],
  ['ETK-XTRA20', 3, 'R02-B01-L01-P01', 'tono', '2026-08-18 10:33'],
  ['ETK-XTRA20', 4, 'R02-B01-L01-P01', 'tono', '2026-08-18 10:34'],
  ['ETK-XTRA20', 5, 'R02-B01-L02-P01', 'tono', '2026-08-18 10:41'],
  ['ETK-XTRA20', 6, 'R02-B01-L02-P01', 'tono', '2026-08-18 10:41'],
  ...Array.from(
    { length: 8 },
    (_, index) =>
      ['ETK-BOLD12', index + 1, 'R01-B01-L01-P01', 'karno', `2026-08-18 11:${pad(40 + index, 2)}`] as [
        string,
        number,
        string,
        'karno',
        string
      ]
  )
]
const DEMO_EMPTY: [string, 'karno' | 'tono', string][] = [
  ['R02-B02-L01-P01', 'tono', '2026-08-18 10:52'],
  ['R02-B02-L02-P01', 'tono', '2026-08-18 10:55']
]
const DEMO_RACKS: Record<string, { operator: 'karno' | 'tono'; sentAt?: string }> = {
  R01: { operator: 'karno' },
  R02: { operator: 'tono', sentAt: '2026-08-18 11:02' },
  R03: { operator: 'karno' }
}

/** Unit berlabel lama di rak Tembakau R01 yang melanggar aturan kategori (antrean "Belum Patuh" TAT-03). */
const MISPLACED: [code: string, qtyBig: number][] = [
  ['CTP-BOLD', 4],
  ['TT-GLDH', 2],
  ['FOIL-GLD70', 3]
]

export function applySetupData(seed: SeedTables): SeedTables {
  const admin = USER_IDS.yulia
  const racks = seed.mt_rack ?? []
  const bins = (seed.mt_rack_bin ?? []).slice().sort((a, b) => String(a.code).localeCompare(String(b.code)))
  const units = seed.mt_unit ?? []
  const materials = seed.mt_material ?? []
  const groups = seed.mt_material_group ?? []
  const binByCode = (code: string) => bins.find(bin => bin.code === code) as LocalRow
  const rackOf = (bin: LocalRow) => racks.find(rack => rack.id === bin.rack_id) as LocalRow
  const unitName = (id: unknown) => String(units.find(unit => unit.id === id)?.name ?? '')
  const unitByName = (name: string, kind: string) =>
    units.find(unit => unit.kind === kind && String(unit.name).toLowerCase() === name.toLowerCase())?.id ?? null
  const occupied = new Set(
    [...(seed.rm_unit ?? [])]
      .filter(unit => unit.unit_status === 'IN_STOCK')
      .map(unit => String(unit.rack_bin_id ?? ''))
      .filter(Boolean)
  )
  const pdPositions = new Set([...DEMO_SCANS.map(scan => scan[2]), ...DEMO_EMPTY.map(scan => scan[0])])
  const freeIn = (rack: string) =>
    bins.filter(
      bin =>
        String(rackOf(bin)?.code) === rack &&
        bin.is_active !== false &&
        !occupied.has(String(bin.id)) &&
        !pdPositions.has(String(bin.code))
    )

  /* ---------- Pindah Material: unit berlabel di rak yang salah + satu tugas pindah ---------- */
  const misplacedBins = freeIn('R01').slice(0, MISPLACED.length)
  if (misplacedBins.length < MISPLACED.length) throw new Error('Seed setup: slot R01 bebas kurang')
  const misplaced: LocalRow[] = MISPLACED.map(([code, qtyBig], index) => {
    const bin = misplacedBins[index]
    occupied.add(String(bin.id))
    const perBig = smallPerBigOf(code)
    return {
      ...audit(admin, '2026-08-05 10:00'),
      id: seedId(SEED_GROUP.rmUnit, 951 + index),
      uid: `UNIT-${pad(951 + index, 6)}`,
      qr_code: `${code}:20260805:01:0001`,
      material_id: materialId(code),
      batch_no: `${code}-260805-01`,
      supplier_id: null,
      po_no: null,
      qty_small: qtyBig * perBig,
      rack_bin_id: bin.id,
      warehouse_zone_id: rackOf(bin).zone_id ?? null,
      flag: 'REGULAR',
      unit_status: 'IN_STOCK',
      unit_big_snapshot: unitName(purchaseUnitOf(code)),
      small_per_big_snapshot: perBig,
      is_leftover_wip: false,
      stored_at: utc('2026-08-05 10:00')
    }
  })
  const flt = misplaced[2] /* FOIL-GLD70 */
  /* Usulan sistem (`suggestBin`): posisi rak umum yang sudah berisi material yang sama → disatukan. */
  const sameMaterialBin = (seed.rm_unit ?? []).find(
    unit =>
      unit.unit_status === 'IN_STOCK' &&
      unit.material_id === flt.material_id &&
      unit.rack_bin_id &&
      !rackOf(bins.find(bin => bin.id === unit.rack_bin_id) as LocalRow)?.category_restriction_id
  )?.rack_bin_id
  /* Tanpa posisi bermaterial sama: posisi rak umum yang kosong (posisi yang ditandai kosong di opname pertama demo). */
  const emptyGeneral = bins.find(
    bin =>
      !rackOf(bin)?.category_restriction_id &&
      bin.is_active !== false &&
      !occupied.has(String(bin.id)) &&
      !DEMO_SCANS.some(scan => scan[2] === bin.code)
  )
  const target = bins.find(bin => bin.id === sameMaterialBin) ?? emptyGeneral
  if (!target) throw new Error('Seed setup: tidak ada slot umum bebas untuk tugas pindah')
  const taskId = seedId(SEED_GROUP.rmMoveTask, 1)
  const rm_move_task: LocalRow[] = [
    {
      ...audit(admin, '2026-08-17 09:00'),
      id: taskId,
      pdh_no: 'PDH-260817-001',
      source_type: 'QUEUE',
      operator_id: USER_IDS.karno,
      by_id: admin,
      task_status: 'ASSIGNED',
      started_at: null,
      completed_at: null
    }
  ]
  const rm_move_task_item: LocalRow[] = [
    {
      ...audit(admin, '2026-08-17 09:00'),
      id: seedId(SEED_GROUP.rmMoveTaskItem, 1),
      task_id: taskId,
      unit_id: flt.id,
      material_id: flt.material_id,
      qty: MISPLACED[2][1],
      from_rack_bin_id: flt.rack_bin_id,
      suggested_rack_bin_id: target.id,
      final_rack_bin_id: null,
      item_status: 'PENDING',
      override_reason: null,
      done_at: null
    }
  ]

  /* ---------- Pendataan Awal: sesi demo dari berkas contoh ---------- */
  const byName = new Map(materials.map(material => [normName(material.name), material]))
  const groupName = (material: LocalRow) =>
    String(groups.find(group => group.id === material.category_id)?.name ?? '').toLowerCase()
  const items = PD_SAMPLE_ROWS.filter(([, , saldo]) => saldo > 0)
  const lines: LocalRow[] = items.map(([no, name, saldo], index) => {
    const material = byName.get(normName(name))
    const fromMaster = material ? PD_FROM_MASTER[groupName(material)] : undefined
    const category: PdCategory =
      fromMaster ??
      ((material && (Object.keys(PD_STD) as PdCategory[]).find(key => key.toLowerCase() === groupName(material))) ||
        pdCategoryOf(name))
    const approved = material ? DEMO_APPROVED[String(material.code)] : undefined
    const proposal = approved ? labelProposal(saldo, approved.qty) : null
    return {
      ...audit(admin, approved ? approved.printedAt : '2026-08-18 08:05'),
      id: seedId(SEED_GROUP.rmOpeningStock, index + 1),
      session_id: pdDemoSessionId,
      line_no: no + 1,
      source_name: name,
      category_name: category,
      qty_source: saldo,
      qty: saldo,
      material_id: material?.id ?? null,
      match_status: material ? 'MATCHED' : 'NEW',
      resolution: material ? 'OK' : null,
      unit_id: material?.default_purchase_unit_id ?? unitByName(PD_STD[category][0], 'PACK'),
      small_unit_id: material?.unit_small_id ?? unitByName(PD_STD[category][2], 'SMALL'),
      qty_per_pack: approved?.qty ?? null,
      pack_plan: proposal?.plan ?? null,
      line_status: proposal ? 'APPROVED' : 'NO_STANDARD',
      printed_at: approved ? utc(approved.printedAt) : null,
      needs_reprint: false,
      batch_no: approved && material ? `${String(material.code)}-260818-01` : null,
      saldo_correction: null,
      is_manual: false
    }
  })

  const pdUnits: LocalRow[] = []
  lines
    .filter(line => line.pack_plan)
    .forEach(line => {
      const material = materials.find(row => row.id === line.material_id) as LocalRow
      const plan = line.pack_plan as { n: number; isi: number }[]
      const contents = plan.flatMap(part => Array.from({ length: part.n }, () => part.isi))
      contents.forEach((isi, index) => {
        const no = 1001 + pdUnits.length
        pdUnits.push({
          ...audit(admin, String(DEMO_APPROVED[String(material.code)].printedAt)),
          id: seedId(SEED_GROUP.rmUnit, no),
          uid: `UNIT-${pad(no, 6)}`,
          qr_code: `${String(material.code)}:20260818:01:${pad(index + 1, 4)}`,
          batch_no: line.batch_no,
          material_id: material.id,
          supplier_id: null,
          po_no: null,
          qty_small: isi,
          rack_bin_id: null,
          warehouse_zone_id: null,
          flag: 'OPENING',
          unit_status: PD_UNIT_STATUS.awaiting,
          qc_date: null,
          unit_big_snapshot: unitName(line.unit_id),
          small_per_big_snapshot: isi,
          is_leftover_wip: false,
          opening_line_id: line.id,
          capture_session_id: pdDemoSessionId,
          stored_at: null
        })
      })
    })
  const unitOf = (code: string, index: number) =>
    pdUnits.find(unit => unit.qr_code === `${code}:20260818:01:${pad(index, 4)}`) as LocalRow

  const initial_capture_session: LocalRow[] = [
    {
      ...audit(admin, '2026-08-18 08:05'),
      id: pdDemoSessionId,
      pdw_no: PD_DEMO_SESSION_NO,
      session_status: 'RUNNING',
      warehouse_id: warehouseRmId,
      import_meta: {
        file_name: PD_SAMPLE_FILE_NAME,
        sheet: PD_SAMPLE_SHEET,
        rows_read: items.length,
        rows_skipped: PD_SAMPLE_ROWS.length - items.length
      },
      opname_started_at: utc('2026-08-18 10:00'),
      opname_started_by: admin,
      closed_at: null,
      closed_by: null,
      approved_at: null,
      approved_by: null,
      return_note: null,
      returned_by: null,
      returned_at: null,
      result: null
    }
  ]
  const initial_capture_area: LocalRow[] = Object.entries(DEMO_RACKS).map(([code, spec], index) => ({
    ...audit(admin, '2026-08-18 09:40'),
    id: seedId(SEED_GROUP.initialCaptureArea, index + 1),
    session_id: pdDemoSessionId,
    rack_id: racks.find(rack => rack.code === code)?.id ?? null,
    operator_id: USER_IDS[spec.operator],
    submitted_at: spec.sentAt ? utc(spec.sentAt) : null
  }))
  const initial_capture_scan: LocalRow[] = [
    ...DEMO_SCANS.map(([code, index, position, operator, at], scanIndex) => ({
      ...audit(USER_IDS[operator], at),
      id: seedId(SEED_GROUP.initialCaptureScan, scanIndex + 1),
      session_id: pdDemoSessionId,
      unit_id: unitOf(code, index).id,
      rack_bin_id: binByCode(position).id,
      result: 'PRESENT',
      reasons: null,
      by_id: USER_IDS[operator],
      at: utc(at),
      undone_at: null
    })),
    ...DEMO_EMPTY.map(([position, operator, at], scanIndex) => ({
      ...audit(USER_IDS[operator], at),
      id: seedId(SEED_GROUP.initialCaptureScan, 100 + scanIndex + 1),
      session_id: pdDemoSessionId,
      unit_id: null,
      rack_bin_id: binByCode(position).id,
      result: 'EMPTY',
      reasons: null,
      by_id: USER_IDS[operator],
      at: utc(at),
      undone_at: null
    }))
  ]

  const tx = (no: number, at: string, user: string, page: string, action: string, ref: string, detail: string) => ({
    ...audit(user, at),
    id: seedId(SEED_GROUP.txLog, 900 + no),
    ts: utc(at),
    log_type: 'Pendataan Awal',
    page_code: page,
    action,
    doc_ref: ref,
    detail,
    warehouse_id: null,
    user_id: user
  })
  const tx_log: LocalRow[] = [
    tx(
      1,
      '2026-08-18 08:05',
      admin,
      'TAT-01',
      'pd-impor',
      PD_DEMO_SESSION_NO,
      `Impor ${PD_SAMPLE_FILE_NAME} (lembar ${PD_SAMPLE_SHEET}): ${items.length} material bersaldo, ${PD_SAMPLE_ROWS.length - items.length} baris saldo 0 dilewati`
    ),
    tx(
      2,
      '2026-08-18 09:15',
      admin,
      'TAT-01',
      'pd-petakan',
      'ETK-BOLD12',
      'Etiket bold 12 PR/PT disetujui: 19 × 33000 + 1 × 28480 pcs'
    ),
    tx(
      3,
      '2026-08-18 09:16',
      admin,
      'TAT-01',
      'pd-petakan',
      'ETK-XTRA20',
      'Etiket Xtra 20 (PT) disetujui: 6 × 33000 pcs'
    ),
    tx(
      4,
      '2026-08-18 09:25',
      admin,
      'TAT-01',
      'pd-cetak',
      PD_DEMO_SESSION_NO,
      `Cetak ${pdUnits.length} stiker baru untuk 2 material (satu per kemasan)`
    ),
    tx(
      5,
      '2026-08-18 10:00',
      admin,
      'TAT-01',
      'pd-mulai-opname',
      PD_DEMO_SESSION_NO,
      'Opname pertama dimulai — 3 rack dibagikan ke operator'
    ),
    tx(
      6,
      '2026-08-18 11:02',
      USER_IDS.tono,
      'TAT-02',
      'pd-kirim',
      PD_DEMO_SESSION_NO,
      'Rack R02 dikirim — 4 posisi dikunjungi, 6 kemasan discan'
    )
  ]

  const mt_reject_type: LocalRow[] = RM_FINDING_REASONS.map(([code, name], index) => ({
    ...audit(admin, '2026-08-01 07:00'),
    id: rmFindingReasonId(code),
    code: `RMF-${code}`,
    name,
    description: null,
    categories: [RM_FINDING_CATEGORY],
    sort: 100 + index,
    is_active: true
  }))

  return {
    ...seed,
    rm_unit: [...(seed.rm_unit ?? []), ...misplaced, ...pdUnits],
    initial_capture_session,
    initial_capture_area,
    initial_capture_scan,
    rm_opening_stock: lines,
    rm_move_task,
    rm_move_task_item,
    tx_log: [...(seed.tx_log ?? []), ...tx_log],
    mt_reject_type: [...(seed.mt_reject_type ?? []), ...mt_reject_type]
  }
}
